import 'react-toastify/dist/ReactToastify.css';
import './styles/app.css';
import Footer from './components/Footer.js';

import { useAuthContext } from './hooks/useAuthContext.js';
import { useLogout } from './hooks/useLogout.js';
import { loadThemeSettings } from './utils/themeGenerator';
import { MaintenanceProvider } from './context/MaintenanceContext';
import MaintenanceBanner from './components/MaintenanceBanner';

import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation, useParams } from 'react-router-dom';
import { ToastContainer, Slide } from 'react-toastify';
import { useEffect, lazy, Suspense } from 'react';
import { usePostHog } from 'posthog-js/react';

const DashboardLayout = lazy(() => import('./pages/DashboardLayout'));
const Landing = lazy(() => import('./pages/Landing.js'));
const Home = lazy(() => import('./pages/Home'));
const Search = lazy(() => import('./pages/Search'));
const Media = lazy(() => import('./pages/Media'));
const AllMedia = lazy(() => import('./pages/AllMedia'));
const Creators = lazy(() => import('./pages/Creators'));
const PersonDetail = lazy(() => import('./pages/PersonDetail'));
const ListDetailPage = lazy(() => import('./pages/ListDetailPage'));
const Details = lazy(() => import('./pages/Details.js'));
const Signup = lazy(() => import('./pages/Signup.js'));
const Login = lazy(() => import('./pages/Login.js'));
const Profile = lazy(() => import('./pages/Profile'));
const Settings = lazy(() => import('./pages/Settings'));
const ForgotPass = lazy(() => import('./pages/ForgotPass'));
const ResetPass = lazy(() => import('./pages/ResetPass'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const PostPage = lazy(() => import('./pages/PostPage'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const AboutContact = lazy(() => import('./pages/AboutContact'));
const StarReceipt = lazy(() => import('./pages/StarReceipt'));
const RequestMediaPage = lazy(() => import('./pages/RequestMediaPage'));
const ForYou = lazy(() => import('./pages/ForYou'));

function RoutePageFallback() {
  return (
    <div className="App" style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
      <p>Loading…</p>
    </div>
  );
}

function ReceiptToCalendarRedirect() {
  const { username } = useParams();
  return <Navigate to={`/calendar/${encodeURIComponent(username)}`} replace />;
}

function PostHogPageView() {
  const location = useLocation();
  const posthog = usePostHog();

  useEffect(() => {
    posthog?.capture('$pageview', { $current_url: window.location.href });
  }, [location, posthog]);

  return null;
}

function ConditionalHeader() {
  return null;
}

function ConditionalFooter() {
  const location = useLocation();
  const publicPaths = ['/', '/login', '/signup', '/forgot-password', '/privacy', '/terms', '/about'];
  const isPublic = publicPaths.includes(location.pathname) || location.pathname.startsWith('/reset-password');
  return isPublic ? <Footer /> : null;
}

function GuestSignupBanner({ user }) {
  const location = useLocation();
  if (user) return null;
  if (location.pathname === '/') return null;

  return (
    <div className="guest-signup-banner" role="region" aria-label="Create account">
      <p className="guest-signup-banner__text">
        Create a free account to save lists, follow people, and track your media.
      </p>
      <Link className="guest-signup-banner__cta" to={`/signup?next=${encodeURIComponent(location.pathname + location.search)}`}>
        Create account
      </Link>
    </div>
  );
}

function App() {
  const { user, authReady } = useAuthContext();
  const { logout } = useLogout();

  useEffect(() => {
    loadThemeSettings();
  }, []);

  useEffect(() => {
    const handleExpiredToken = (event) => {
      if (event.detail?.error?.includes('Session expired') ||
          event.detail?.error?.includes('Invalid token')) {
        const currentPath = window.location.pathname;
        if (currentPath !== '/login' && currentPath !== '/signup') {
          sessionStorage.setItem('redirectAfterLogin', currentPath);
        }
        logout();
      }
    };

    window.addEventListener('auth-error', handleExpiredToken);

    return () => {
      window.removeEventListener('auth-error', handleExpiredToken);
    };
  }, [logout]);

  if (!authReady) {
    return (
      <div className="App" style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <p>Loading Mytria...</p>
      </div>
    );
  }

  return (
    <MaintenanceProvider>
      <Router>
        <PostHogPageView />
        <div className={`App${!user ? ' app--guest' : ''}`}>
            <ToastContainer position="top-center"
              autoClose={2000}
              transition={Slide}
              hideProgressBar
              newestOnTop={false}
              closeOnClick={false}
              rtl={false}
              pauseOnFocusLoss
              pauseOnHover
              theme="dark"
              toastClassName="app-toast"
            />
            <MaintenanceBanner />
            <GuestSignupBanner user={user} />

            <ConditionalHeader />
            {authReady && (
            <Suspense fallback={<RoutePageFallback />}>
            <Routes>
              <Route path="/" element={user ? <Navigate to="/home" /> : <Landing />} />

              <Route path="/signup" element={!user ? <Signup /> : <Navigate to="/home" />} />
              <Route path="/login" element={!user ? <Login /> : <Navigate to="/home" />} />
              <Route path="/forgot-password" element={!user ? <ForgotPass /> : <Navigate to="/home" />} />
              <Route path="/reset-password/:token" element={!user ? <ResetPass /> : <Navigate to="/home" />} />

              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/about" element={<AboutContact />} />

              <Route element={<DashboardLayout />}>
                <Route path="/home" element={user ? <Home /> : <Navigate to="/login" />} />
                <Route path="/search" element={user ? <Search /> : <Navigate to="/login" />} />
                <Route path="/media" element={user ? <Media /> : <Navigate to="/login" />} />
                <Route path="/for-you" element={user ? <ForYou /> : <Navigate to="/login" />} />
                <Route
                  path="/saved"
                  element={
                    user ? (
                      <Navigate to={`/profile/${encodeURIComponent(user.username)}?tab=saved`} replace />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route path="/request-media" element={user ? <RequestMediaPage /> : <Navigate to="/login" />} />
                <Route path="/all-media" element={user ? <AllMedia /> : <Navigate to="/login" />} />
                <Route path="/creators" element={user ? <Creators /> : <Navigate to="/login" />} />
                <Route path="/person/:sourcePersonId" element={user ? <PersonDetail /> : <Navigate to="/login" />} />
                <Route path="/posts/:postId" element={user ? <PostPage /> : <Navigate to="/login" />} />
                <Route path="/calendar/:username" element={<StarReceipt />} />
                <Route path="/receipt/:username" element={<ReceiptToCalendarRedirect />} />
                <Route path="/lists/:listId" element={<ListDetailPage />} />
                <Route path="/lists/:listId/:mediaId" element={<Details />} />
                <Route path="/:type/:sourceMediaId" element={<Details />} />
                <Route path="/profile/:username" element={<Profile />} />
                <Route path="/settings" element={user ? <Settings /> : <Navigate to="/login" />} />
                <Route path="/onboarding" element={user ? <Onboarding /> : <Navigate to="/signup" />} />
              </Route>
            </Routes>
            </Suspense>
            )}
            <ConditionalFooter />
          </div>
        </Router>
      </MaintenanceProvider>
  );
}

export default App;
